import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  TrendingUp, 
  TrendingDown,
  Bitcoin,
  LineChart,
  Bot,
  Sparkles,
  Pencil,
  Target,
  Search
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer 
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

const POPULAR_STOCKS = ["AAPL", "GOOGL", "MSFT", "AMZN", "TSLA", "NVDA", "META"];
const POPULAR_CRYPTO = ["BTC", "ETH", "SOL", "XRP", "ADA", "DOT", "AVAX"];

export default function Investments() {
  const { api } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [deepDiveOpen, setDeepDiveOpen] = useState(false);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  const [deepDiveResult, setDeepDiveResult] = useState(null);
  const [deepDiveSymbol, setDeepDiveSymbol] = useState("");
  const [deepDiveType, setDeepDiveType] = useState("stock");
  const [formData, setFormData] = useState({
    symbol: "",
    name: "",
    investment_type: "",
    quantity: "",
    purchase_price: "",
    purchase_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const res = await api.get("/investments");
      setInvestments(res.data);
    } catch (error) {
      toast.error("Failed to fetch investments");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/investments", {
        ...formData,
        quantity: parseFloat(formData.quantity),
        purchase_price: parseFloat(formData.purchase_price)
      });
      toast.success("Investment added");
      setDialogOpen(false);
      setFormData({
        symbol: "",
        name: "",
        investment_type: "",
        quantity: "",
        purchase_price: "",
        purchase_date: new Date().toISOString().split('T')[0]
      });
      fetchInvestments();
    } catch (error) {
      toast.error("Failed to add investment");
    }
  };

  const handleEdit = (investment) => {
    setEditingInvestment(investment);
    setFormData({
      symbol: investment.symbol,
      name: investment.name,
      investment_type: investment.investment_type,
      quantity: investment.quantity.toString(),
      purchase_price: investment.purchase_price.toString(),
      purchase_date: investment.purchase_date || new Date().toISOString().split('T')[0]
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingInvestment) return;
    
    try {
      await api.put(`/investments/${editingInvestment.id}`, {
        ...formData,
        quantity: parseFloat(formData.quantity),
        purchase_price: parseFloat(formData.purchase_price)
      });
      toast.success("Investment updated");
      setEditDialogOpen(false);
      setEditingInvestment(null);
      setFormData({
        symbol: "",
        name: "",
        investment_type: "",
        quantity: "",
        purchase_price: "",
        purchase_date: new Date().toISOString().split('T')[0]
      });
      fetchInvestments();
    } catch (error) {
      toast.error("Failed to update investment");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this investment?")) return;
    try {
      await api.delete(`/investments/${id}`);
      toast.success("Investment deleted");
      fetchInvestments();
    } catch (error) {
      toast.error("Failed to delete investment");
    }
  };

  const handleAiAdvice = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/ai/portfolio-advice");
      setAiResult(res.data.analysis);
    } catch (error) {
      toast.error("AI analysis failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleDeepDive = async (symbol, type) => {
    const sym = symbol || deepDiveSymbol;
    const invType = type || deepDiveType;
    if (!sym) {
      toast.error("Please enter a symbol");
      return;
    }
    setDeepDiveSymbol(sym);
    setDeepDiveType(invType);
    setDeepDiveLoading(true);
    setDeepDiveResult(null);
    setDeepDiveOpen(true);
    
    try {
      const res = await api.post(`/ai/investment-analysis?symbol=${sym}&investment_type=${invType}`);
      setDeepDiveResult(res.data);
    } catch (error) {
      toast.error("Deep dive analysis failed");
      setDeepDiveResult({ error: "Analysis failed. Please try again." });
    } finally {
      setDeepDiveLoading(false);
    }
  };

  const stocks = investments.filter(i => i.investment_type === "stock");
  const crypto = investments.filter(i => i.investment_type === "crypto");
  
  const totalStockValue = stocks.reduce((sum, i) => sum + (i.quantity * i.current_price), 0);
  const totalCryptoValue = crypto.reduce((sum, i) => sum + (i.quantity * i.current_price), 0);
  const totalValue = totalStockValue + totalCryptoValue;
  const totalGainLoss = investments.reduce((sum, i) => sum + i.gain_loss, 0);

  // Simulated performance data
  const performanceData = [
    { month: 'Jan', value: totalValue * 0.85 },
    { month: 'Feb', value: totalValue * 0.88 },
    { month: 'Mar', value: totalValue * 0.92 },
    { month: 'Apr', value: totalValue * 0.95 },
    { month: 'May', value: totalValue * 0.98 },
    { month: 'Jun', value: totalValue },
  ];

  return (
    <Layout title="Investments">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-portfolio-card">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Total Portfolio</p>
              <p className="text-3xl font-bold text-violet-500 font-mono">{formatCurrency(totalValue)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="stocks-value-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <LineChart className="w-4 h-4 text-blue-500" />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Stocks</p>
              </div>
              <p className="text-2xl font-bold text-blue-500 font-mono">{formatCurrency(totalStockValue)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="crypto-value-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Bitcoin className="w-4 h-4 text-orange-500" />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Crypto</p>
              </div>
              <p className="text-2xl font-bold text-orange-500 font-mono">{formatCurrency(totalCryptoValue)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-gain-loss-card">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Total Gain/Loss</p>
              <p className={`text-3xl font-bold font-mono ${totalGainLoss >= 0 ? 'text-primary' : 'text-red-500'}`}>
                {totalGainLoss >= 0 ? '+' : ''}{formatCurrency(totalGainLoss)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                data-testid="add-investment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Investment
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                  Add Investment
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Type</Label>
                  <Select
                    value={formData.investment_type}
                    onValueChange={(value) => setFormData({ ...formData, investment_type: value, symbol: "" })}
                    required
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="investment-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="stock">Stock</SelectItem>
                      <SelectItem value="crypto">Cryptocurrency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Symbol</Label>
                  <Input
                    value={formData.symbol}
                    onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                    placeholder={formData.investment_type === "stock" ? "e.g., AAPL, GOOGL" : "e.g., BTC, ETH"}
                    required
                    data-testid="investment-symbol-input"
                    className="bg-black/50 border-white/10 rounded-sm font-mono"
                  />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(formData.investment_type === "stock" ? POPULAR_STOCKS : POPULAR_CRYPTO).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setFormData({ ...formData, symbol: s })}
                        className={`text-xs px-2 py-1 border transition-colors ${
                          formData.symbol === s 
                            ? 'border-violet-500 bg-violet-500/20 text-violet-400' 
                            : 'border-white/10 text-zinc-500 hover:border-white/20 hover:text-zinc-300'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Apple Inc."
                    required
                    data-testid="investment-name-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Quantity</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      placeholder="0"
                      required
                      data-testid="investment-quantity-input"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Purchase Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="investment-price-input"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                  data-testid="submit-investment-btn"
                >
                  Add Investment
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm"
                data-testid="ai-portfolio-advice-btn"
              >
                <Bot className="w-4 h-4 mr-2" />
                AI Portfolio Advice
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-violet-500/30 rounded-none max-w-2xl ai-active">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Sparkles className="w-5 h-5 text-violet-500" />
                  AI Portfolio Analysis
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-white/5 border border-white/10">
                  <p className="text-sm text-zinc-400">Current Holdings: <span className="text-white">{investments.length}</span></p>
                  <p className="text-sm text-zinc-400">Portfolio Value: <span className="text-violet-400 font-mono">{formatCurrency(totalValue)}</span></p>
                </div>
                <Button
                  onClick={handleAiAdvice}
                  disabled={aiLoading}
                  className="w-full bg-violet-500 text-white hover:bg-violet-600 rounded-sm"
                  data-testid="get-ai-advice-btn"
                >
                  {aiLoading ? "Analyzing..." : "Get AI Advice"}
                </Button>
                {aiResult && (
                  <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-sm max-h-96 overflow-y-auto">
                    <p className="text-xs font-mono uppercase tracking-widest text-violet-400 mb-2">AI Recommendations</p>
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap">{aiResult}</div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* AI Deep Dive Button */}
          <Button 
            variant="outline"
            onClick={() => setDeepDiveOpen(true)}
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 rounded-sm"
            data-testid="ai-deep-dive-btn"
          >
            <Target className="w-4 h-4 mr-2" />
            AI Deep Dive
          </Button>
        </div>

        {/* AI Deep Dive Dialog */}
        <Dialog open={deepDiveOpen} onOpenChange={setDeepDiveOpen}>
          <DialogContent className="bg-[#0A0A0A] border-emerald-500/30 rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Target className="w-5 h-5 text-emerald-500" />
                AI Deep Dive Analysis
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!deepDiveResult && !deepDiveLoading && (
                <>
                  <p className="text-sm text-zinc-400">
                    Get buy/hold/sell recommendations with financial analysis.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Type</Label>
                      <Select value={deepDiveType} onValueChange={setDeepDiveType}>
                        <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          <SelectItem value="stock">Stock</SelectItem>
                          <SelectItem value="crypto">Cryptocurrency</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Symbol</Label>
                      <Input
                        value={deepDiveSymbol}
                        onChange={(e) => setDeepDiveSymbol(e.target.value.toUpperCase())}
                        placeholder={deepDiveType === "stock" ? "AAPL, NVDA" : "BTC, ETH"}
                        className="bg-black/50 border-white/10 rounded-sm font-mono"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(deepDiveType === "stock" ? POPULAR_STOCKS : POPULAR_CRYPTO).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setDeepDiveSymbol(s)}
                        className={`text-xs px-2 py-1 border transition-colors ${
                          deepDiveSymbol === s 
                            ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400' 
                            : 'border-white/10 text-zinc-500 hover:text-zinc-300'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <Button
                    onClick={() => handleDeepDive()}
                    disabled={!deepDiveSymbol}
                    className="w-full bg-emerald-500 text-white hover:bg-emerald-600 rounded-sm"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Run Analysis
                  </Button>
                </>
              )}
              
              {deepDiveLoading && (
                <div className="py-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent mb-4"></div>
                  <p className="text-zinc-400">Analyzing {deepDiveSymbol}...</p>
                </div>
              )}
              
              {deepDiveResult && !deepDiveResult.error && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10">
                    <div>
                      <p className="text-xl font-bold text-white font-mono">{deepDiveResult.symbol}</p>
                      <p className="text-sm text-zinc-500">{deepDiveResult.investment_type}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-zinc-500">Current Price</p>
                      <p className="text-xl font-bold text-emerald-400 font-mono">
                        {formatCurrency(deepDiveResult.current_price || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-sm max-h-[45vh] overflow-y-auto">
                    <p className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-3">AI Analysis</p>
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{deepDiveResult.analysis}</div>
                  </div>
                  <Button
                    onClick={() => setDeepDiveResult(null)}
                    variant="outline"
                    className="w-full border-white/20 text-zinc-400 hover:bg-white/5 rounded-sm"
                  >
                    Analyze Another
                  </Button>
                </div>
              )}
              
              {deepDiveResult?.error && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                  <p className="text-red-400">{deepDiveResult.error}</p>
                  <Button
                    onClick={() => setDeepDiveResult(null)}
                    variant="outline"
                    className="mt-4 w-full border-white/20 text-zinc-400 rounded-sm"
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Performance Chart */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="performance-chart">
          <CardHeader>
            <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
              Portfolio Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={performanceData}>
                  <defs>
                    <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#7C3AED" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    stroke="#52525B" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#27272A' }}
                  />
                  <YAxis 
                    stroke="#52525B" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#27272A' }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#0A0A0A', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '0',
                      fontSize: '12px',
                      fontFamily: 'JetBrains Mono'
                    }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#7C3AED" 
                    fillOpacity={1} 
                    fill="url(#valueGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Holdings */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="holdings-list">
          <CardHeader>
            <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
              Holdings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-zinc-500">Loading...</div>
            ) : investments.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No investments yet. Add your first holding!
              </div>
            ) : (
              <div className="space-y-2">
                {investments.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-4 border border-white/5 hover:border-white/10 transition-colors"
                    data-testid={`investment-item-${inv.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded flex items-center justify-center ${inv.investment_type === 'stock' ? 'bg-blue-500/20' : 'bg-orange-500/20'}`}>
                        {inv.investment_type === 'stock' 
                          ? <LineChart className="w-5 h-5 text-blue-500" />
                          : <Bitcoin className="w-5 h-5 text-orange-500" />
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-white font-mono">{inv.symbol}</p>
                          <span className={`text-xs px-2 py-0.5 ${inv.investment_type === 'stock' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
                            {inv.investment_type}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-500">{inv.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-zinc-500">Qty: {inv.quantity}</p>
                        <p className="text-xs text-zinc-600">@ {formatCurrency(inv.purchase_price)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-white font-mono">
                          {formatCurrency(inv.quantity * inv.current_price)}
                        </p>
                        <div className={`flex items-center justify-end gap-1 text-sm ${inv.gain_loss >= 0 ? 'text-primary' : 'text-red-500'}`}>
                          {inv.gain_loss >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          <span className="font-mono">
                            {inv.gain_loss >= 0 ? '+' : ''}{inv.gain_loss_percent.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeepDive(inv.symbol, inv.investment_type)}
                          className="text-zinc-500 hover:text-emerald-500 hover:bg-emerald-500/10"
                          title="AI Deep Dive Analysis"
                        >
                          <Target className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(inv)}
                          className="text-zinc-500 hover:text-blue-500 hover:bg-blue-500/10"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(inv.id)}
                          className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10"
                          data-testid={`delete-investment-${inv.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
